import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Area, AreaChart, Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { BarChart3, PieChart as PieIcon, TrendingUp } from "lucide-react";
import { useData } from "@/context/DataContext";
import { useStats } from "@/hooks/useStats";
import { ds, money } from "@/lib/money";
import { t } from "@/lib/i18n";

const TABS = [["where", "Where it went", BarChart3], ["pie", "Pie chart", PieIcon], ["trend", "Trend", TrendingUp]];

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-xl border border-[var(--bd)] bg-[var(--card)] px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold">{p.payload.label || label}</div>
      <div className="num font-bold" style={{ color: p.payload.c || "var(--ac)" }}>{money(p.value)}</div>
    </div>
  );
}

function Where({ rows, total }) {
  return (
    <div data-testid="chart-where-it-went">
      <div className="mb-5 h-56" data-testid="chart-where-bar-graph">
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--bd)" strokeDasharray="3 4" />
            <XAxis dataKey="e" hide />
            <YAxis tick={{ fill: "var(--mu)", fontSize: 11 }} axisLine={false} tickLine={false} width={50} />
            <Tooltip content={<Tip />} cursor={{ fill: "color-mix(in srgb, var(--ac) 10%, transparent)" }} />
            <Bar dataKey="value" radius={[8, 8, 2, 2]} animationDuration={700}>{rows.map((r) => <Cell key={r.id} fill={r.c} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-3">
      {rows.map((r, i) => (
        <div key={r.id} className="group">
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="flex items-center gap-2"><span className="text-lg transition-transform group-hover:scale-125">{r.e}</span>{r.label}</span>
            <span className="num text-[13px]"><b>{money(r.value)}</b> <span className="text-[var(--mu)]">{((r.value / total) * 100).toFixed(0)}%</span></span>
          </div>
          <div className="mm-bar !h-2.5"><motion.i initial={{ width: 0 }} animate={{ width: `${(r.value / rows[0].value) * 100}%` }} transition={{ duration: 0.7, delay: i * 0.05 }} style={{ background: r.c }} /></div>
        </div>
      ))}
      </div>
    </div>
  );
}

const Active = (p) => (
  <g>
    <Sector {...p} outerRadius={p.outerRadius + 8} />
    <Sector {...p} innerRadius={p.outerRadius + 11} outerRadius={p.outerRadius + 14} />
  </g>
);

function Donut({ rows, total }) {
  const [ai, setAi] = useState(0);
  const a = rows[ai] || rows[0];
  return (
    <div className="grid items-center gap-4 sm:grid-cols-[1fr_1fr]" data-testid="chart-pie">
      <div className="relative h-60">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={rows} dataKey="value" innerRadius="58%" outerRadius="80%" paddingAngle={2} activeIndex={ai} activeShape={Active} onMouseEnter={(_, i) => setAi(i)} onClick={(_, i) => setAi(i)} stroke="none">
              {rows.map((r) => <Cell key={r.id} fill={r.c} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div><div className="text-2xl">{a.e}</div><div className="num text-sm font-bold">{money(a.value)}</div><div className="text-[11px] text-[var(--mu)]">{((a.value / total) * 100).toFixed(1)}%</div></div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {rows.map((r, i) => (
          <button key={r.id} onMouseEnter={() => setAi(i)} onClick={() => setAi(i)} className={`mm-chip !py-1 text-xs ${i === ai ? "!border-[var(--tx)]" : ""}`}>
            <i className="h-2.5 w-2.5 rounded-full" style={{ background: r.c }} />{r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Trend({ m, s, e, mode }) {
  const data = useMemo(() => {
    const yr = mode === "year";
    const n = yr ? 12 : Math.round((e - s) / 864e5);
    const v = Array.from({ length: n }, (_, k) => {
      const d = yr ? new Date(s.getFullYear(), k, 1) : new Date(s.getFullYear(), s.getMonth(), s.getDate() + k);
      return { k, value: 0, label: yr ? ds(d, { month: "long" }) : ds(d, { weekday: "short", day: "numeric", month: "short" }), x: yr ? ds(d, { month: "short" }) : String(d.getDate()) };
    });
    m.forEach((i) => { const d = new Date(i.date + "T00:00"); const k = yr ? d.getMonth() : Math.round((d - s) / 864e5); if (v[k]) v[k].value += i.amt; });
    return v;
  }, [m, s, e, mode]);
  const Chart = mode === "week" ? BarChart : AreaChart;
  return (
    <div className="h-60 min-w-0 overflow-hidden" data-testid="chart-trend">
      <ResponsiveContainer>
        <Chart data={data} margin={{ top: 10, right: 6, left: -18, bottom: 0 }}>
          <defs><linearGradient id="mmTr" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="var(--ac)" stopOpacity={0.45} /><stop offset="1" stopColor="var(--ac)" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid vertical={false} stroke="var(--bd)" strokeDasharray="3 4" />
          <XAxis dataKey="x" tick={{ fill: "var(--mu)", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={14} />
          <YAxis tick={{ fill: "var(--mu)", fontSize: 11 }} axisLine={false} tickLine={false} width={50} />
          <Tooltip content={<Tip />} cursor={{ stroke: "var(--ac)", strokeOpacity: 0.3, fill: "color-mix(in srgb, var(--ac) 10%, transparent)" }} />
          {mode === "week" ? <Bar dataKey="value" fill="var(--ac)" radius={[8, 8, 2, 2]} /> : <Area type="monotone" dataKey="value" stroke="var(--ac)" strokeWidth={2.5} fill="url(#mmTr)" activeDot={{ r: 5, strokeWidth: 0 }} />}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Charts() {
  const { catInfo, mode } = useData();
  const st = useStats();
  const [tab, setTab] = useState("where");
  const rows = Object.entries(st.byCat).map(([id, value]) => { const c = catInfo(id); return { id, value, label: c.n, e: c.e, c: c.c }; }).sort((a, b) => b.value - a.value);
  return (
    <section className="mm-card mm-rise min-w-0 overflow-hidden" data-testid="charts-card">
      <div className="no-scrollbar mb-5 flex gap-1 overflow-x-auto">
        {TABS.map(([k, l, I]) => (
          <button key={k} className={`mm-chip ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} data-testid={`charts-tab-${k}`}><I className="h-3.5 w-3.5" />{t(l)}</button>
        ))}
      </div>
      {!rows.length ? <p className="py-6 text-center text-sm text-[var(--mu)]" data-testid="charts-empty">{t("Nothing spent in this period yet.")}</p> :
        tab === "where" ? <Where rows={rows} total={st.total} /> : tab === "pie" ? <Donut rows={rows} total={st.total} /> : <Trend m={st.m} s={st.s} e={st.e} mode={mode} />}
    </section>
  );
}
